/**
 * MessageBubble — renders a single user or assistant message.
 *
 * Assistant messages render Markdown with syntax-highlighted code blocks.
 * User messages render as plain text with whitespace preserved.
 */

/** Pretty-print the stored ``messages.model`` value for the footer badge.
 *
 * Internal IDs like ``openai-gpt-4o-mini`` collapse to ``GPT-4o Mini``;
 * Anthropic IDs stay close to their published name. Returns ``null`` for
 * empty/unrecognised values so historical rows (pre-migration NULL) just
 * don't render a badge instead of showing "unknown". */
function displayModelLabel(model: string | null | undefined): string | null {
  if (!model) return null;
  // Anthropic
  if (model === "claude-fable-5-1") return "Claude Fable 5.1";
  if (model === "claude-fable-5") return "Claude Fable 5";
  if (model === "claude-opus-5") return "Claude Opus 5";
  if (model === "claude-opus-4-8") return "Claude Opus 4.8";
  if (model === "claude-opus-4-7") return "Claude Opus 4.7";
  if (model === "claude-opus-4-6") return "Claude Opus 4.6";
  if (model === "claude-sonnet-4-6") return "Claude Sonnet 4.6";
  if (model.startsWith("claude-haiku-4-5")) return "Claude Haiku 4.5";
  // OpenRouter ids carry the upstream slug after the prefix —
  // ``openrouter/anthropic/claude-opus-4.8`` → ``anthropic/claude-opus-4.8``.
  if (model.startsWith("openrouter/")) return model.slice("openrouter/".length);
  // OpenAI lineup uses dash-only internal IDs; map back to dotted display names.
  if (model.startsWith("openai-")) {
    const stripped = model.slice("openai-".length);
    // Replace the LAST occurrence of `-N-M` with `.N.M` for gpt-5-5 / gpt-4-1 etc.
    const dotted = stripped.replace(/^gpt-(\d)-(\d)/, "gpt-$1.$2");
    // Capitalise gpt → GPT for nicer typography.
    return dotted.replace(/^gpt-/i, "GPT-").replace(/-mini$/, " Mini").replace(/-nano$/, " Nano").replace(/-pro$/, " Pro");
  }
  return model;
}

/** Provider family → human label for the "via <Provider>" clause. */
const PROVIDER_DISPLAY: Record<string, string> = {
  claude: "Anthropic",
  openrouter: "OpenRouter",
  openai: "OpenAI",
  gemini: "Gemini",
};

import { useState, useEffect, useMemo, useRef, lazy, Suspense, memo, createContext, useContext } from "react";
import type { Message, ShortlistData, ModelSwitchBlock, EnvSwitchBlock, ThinkingContentBlock } from "@/api/types";
import { parseUTC } from "@/api/types";
import { fetchTtsBlobUrl } from "@/api/audio";
import { useChatStore } from "@/stores/chatStore";
import { useThemeStore } from "@/stores/themeStore";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ProviderIcon, getProvider } from "@/components/common/ProviderLogos";
import { useSubagentDrillStore } from "@/stores/subagentDrillStore";
import { ShortlistReadyCard } from "./ShortlistReadyCard";
import { MessageThinkingPanel } from "./ThinkingPanel";

// True while rendering a live stream. Lets the code renderer skip Prism
// highlighting mid-stream (it would re-highlight the whole growing block every
// frame); finalised bubbles highlight once.
const StreamingCtx = createContext(false);

// Shared by the markdown render and the streaming render so the live draft and
// the finalised bubble share typography (no reflow on hand-off).
const ASSISTANT_PROSE_CLASS = `prose prose-sm max-w-none break-words overflow-hidden
  prose-p:my-1.5 prose-p:leading-relaxed
  prose-headings:my-2 prose-headings:font-semibold
  prose-h3:text-sm prose-h4:text-xs
  prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
  prose-strong:font-semibold prose-strong:text-space
  prose-a:text-jove prose-a:no-underline
  prose-code:text-code-ink prose-code:bg-jove-10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
  prose-pre:my-2 prose-pre:p-0 prose-pre:bg-transparent
  prose-blockquote:border-l-jove-60 prose-blockquote:text-space-50
  prose-hr:border-space-30 prose-hr:my-4`;

// Lazy-load syntax highlighter — only loaded when code blocks appear
const SyntaxHighlighter = lazy(() =>
  import("react-syntax-highlighter").then((m) => ({ default: m.Prism })),
);

// Module-level style cache — both Prism themes loaded once (same module, so no
// extra network cost) and shared across all LazyCodeBlock instances. We pick
// light/dark per the active theme; a hardcoded light theme was unreadable-looking
// (a bright block) in dark mode.
type PrismStyle = Record<string, React.CSSProperties>;
// The Prism themes give `code`/`pre` their own background; with our own block
// bg on the container that inner background shows as a lighter block hugging
// the text (reads like a selection highlight). Strip it so the block colour
// comes solely from `customStyle`.
function stripThemeBg(style: PrismStyle): PrismStyle {
  const clear = { background: "transparent", backgroundColor: "transparent" };
  return {
    ...style,
    'code[class*="language-"]': { ...style['code[class*="language-"]'], ...clear },
    'pre[class*="language-"]': { ...style['pre[class*="language-"]'], ...clear },
  };
}
let _stylesCache: { light: PrismStyle; dark: PrismStyle } | null = null;
const _stylesPromise = import("react-syntax-highlighter/dist/esm/styles/prism").then((m) => {
  _stylesCache = { light: stripThemeBg(m.oneLight), dark: stripThemeBg(m.oneDark) };
  return _stylesCache;
});

function LazyCodeBlock({ language, code }: { language: string; code: string }) {
  const isDark = useThemeStore((s) => s.resolved) === "dark";
  const [styles, setStyles] = useState(_stylesCache);

  useEffect(() => {
    if (!styles) {
      _stylesPromise.then(setStyles);
    }
  }, [styles]);

  if (!styles) {
    return <pre className="p-3 text-xs bg-jove-10 text-space font-mono leading-relaxed overflow-x-auto">{code}</pre>;
  }

  // Warm-dark block background (a touch darker than the surface so the block
  // reads as inset); the jove-10 lilac in light.
  const bg = isDark ? "#1C1B18" : "#FAF9FF";
  return (
    <SyntaxHighlighter
      style={isDark ? styles.dark : styles.light}
      language={language}
      PreTag="div"
      customStyle={{ margin: 0, padding: "12px", fontSize: "12px", lineHeight: "1.6", background: bg, overflowX: "auto" }}
    >
      {code}
    </SyntaxHighlighter>
  );
}

interface Props {
  message: Message;
  /** Render the text as a live word-fade stream instead of markdown. */
  streaming?: boolean;
  /** Play the fadeInUp entry animation. Off for a just-streamed message whose
   *  content is already on screen, so the persisted bubble swaps in seamlessly
   *  instead of replaying a bubble-up. */
  entranceAnimation?: boolean;
  /** Briefly flash a ring around the bubble — used when the user jumps to this
   *  message from search, so the eye lands on the right row (WhatsApp-style). */
  highlight?: boolean;
  /** Suppress the RLHF thumbs + note. Set by the sub-agent drill-in modal:
   *  rating a sub-agent's internal step isn't meaningful (the vote belongs on
   *  the main answer) and the row isn't in the main transcript anyway. */
  hideFeedback?: boolean;
}

/** Bubble width.
 *
 *  Full-bleed on phones, capped on desktop. The 80% cap exists so a wide
 *  desktop column still reads as a conversation (alignment + a gutter), but on
 *  a phone that gutter is dead space: it forced needless wrapping — and hurt
 *  worst on exactly the content that needs room (tables, code, shortlists).
 *  Below `md` the fill colour alone carries the user-vs-agent distinction,
 *  which is enough since the two alternate.
 *
 *  `min-w-0` lets the flex child shrink below its content's intrinsic width, so
 *  a long unbroken token (a URL, a wide table) scrolls inside its own container
 *  instead of stretching the bubble past the viewport.
 */
export const BUBBLE_WIDTH = "w-full min-w-0 md:w-auto md:max-w-[80%]";

/** Same, for chat-column *cards* that should fill their share rather than hug
 *  their content (a summary card looks wrong shrink-wrapped). Identical on
 *  mobile; on desktop it stays full-width up to the 80% cap. */
export const BUBBLE_WIDTH_FILL = "w-full min-w-0 md:max-w-[80%]";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API unavailable (PyWebView, non-HTTPS, or permission denied)
      // — fall back to legacy execCommand approach.
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy code"}
      aria-label={copied ? "Copied!" : "Copy code"}
      className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1
                 text-[10px] font-medium rounded-md
                 bg-surface/85 backdrop-blur-sm text-space-60 hover:text-space
                 border border-space-30 hover:border-jove-100
                 shadow-sm hover:shadow transition-all duration-150"
    >
      {copied ? (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

function BubbleCopyButton({ text, isUser }: { text: string; isUser: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 p-0.5 rounded text-[10px]
        ${isUser ? "text-white/70 hover:text-white" : "text-space-50 hover:text-space"}`}
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

/**
 * Read-aloud control for assistant messages.
 *
 * Hits ``POST /api/audio/tts`` lazily on first click — that's also when
 * the backend triggers the one-time model download, so the first play
 * after install takes a few seconds. The blob URL is cached for the
 * lifetime of the bubble so a repeat click is instant.
 */
function SpeakButton({ text }: { text: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "error">("idle");

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const handleClick = async () => {
    if (state === "playing") {
      audioRef.current?.pause();
      setState("idle");
      return;
    }

    try {
      if (!urlRef.current) {
        setState("loading");
        urlRef.current = await fetchTtsBlobUrl(text);
      }
      let audio = audioRef.current;
      if (!audio) {
        audio = new Audio(urlRef.current);
        audio.onended = () => setState("idle");
        audio.onerror = () => setState("error");
        audioRef.current = audio;
      } else {
        audio.currentTime = 0;
      }
      await audio.play();
      setState("playing");
    } catch (e) {
      console.error("TTS playback failed:", e);
      setState("error");
    }
  };

  const label =
    state === "loading"
      ? "Loading…"
      : state === "playing"
        ? "Stop"
        : state === "error"
          ? "TTS failed"
          : "Read aloud";

  return (
    <button
      onClick={handleClick}
      disabled={state === "loading"}
      className="flex items-center gap-1 p-0.5 rounded text-[10px] text-space-50 hover:text-space disabled:opacity-50"
      title={label}
      aria-label={label}
    >
      {state === "playing" ? (
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M19.07 4.93a10 10 0 010 14.14M6.343 9H4a1 1 0 00-1 1v4a1 1 0 001 1h2.343l4.95 4.243A.6.6 0 0012.3 19V5a.6.6 0 00-1.007-.443L6.343 9z" />
        </svg>
      )}
      {state === "loading" ? "…" : null}
    </button>
  );
}

// Long pasted text (job descriptions, CVs, logs) would otherwise turn a user
// bubble into a wall that buries the rest of the thread. We collapse it to a
// compact preview behind a "Show more" toggle. Tuned so an ordinary multi-line
// message stays fully visible and only genuinely long pastes fold.
const PREVIEW_CHAR_LIMIT = 600; // target length of the collapsed preview
const PREVIEW_LINE_LIMIT = 10; // …or this many lines, whichever trips first
const MIN_HIDDEN_CHARS = 120; // don't add a CTA when it would hide only a sliver

/**
 * Build a word/line-boundary-aware preview of a long message.
 *
 * Caps by both characters and lines so a single wall of text *and* a tall
 * paste of many short lines (logs, lists) each collapse to a compact block.
 * The character cut is pulled back to the nearest whitespace so we never
 * slice a word in half — unless that boundary is so early it would strand
 * most of the preview (e.g. one giant unbroken token), in which case we keep
 * the hard cut and let the bubble's ``break-words`` wrap it.
 */
function buildPreview(text: string): { preview: string; truncated: boolean } {
  let working = text;
  let truncated = false;

  const lines = working.split("\n");
  if (lines.length > PREVIEW_LINE_LIMIT) {
    working = lines.slice(0, PREVIEW_LINE_LIMIT).join("\n");
    truncated = true;
  }

  if (working.length > PREVIEW_CHAR_LIMIT) {
    const slice = working.slice(0, PREVIEW_CHAR_LIMIT);
    const boundary = Math.max(slice.lastIndexOf(" "), slice.lastIndexOf("\n"));
    working = boundary > PREVIEW_CHAR_LIMIT * 0.6 ? slice.slice(0, boundary) : slice;
    truncated = true;
  }

  return { preview: working.trimEnd(), truncated };
}

/**
 * User-message text with a "Show more"/"Show less" toggle for long content.
 *
 * Falls back to a plain paragraph when the text is short or when collapsing
 * would hide too little to be worth a CTA. ``onDark`` switches the toggle
 * colours for the gradient (white text) vs. queued (light) bubble.
 *
 * Collapsing a bubble taller than the viewport otherwise dumps the user far
 * below it: this row lives inside react-virtuoso, which holds ``scrollTop``
 * constant while the row shrinks, so the position that was near the bottom of
 * the huge bubble now points at content well past it. We fix that by anchoring
 * the toggle button's on-screen position across the collapse — see
 * ``handleToggle`` — so the bubble the user just collapsed stays put under
 * their cursor.
 */
function CollapsibleText({ text, onDark }: { text: string; onDark: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const { preview, collapsible } = useMemo(() => {
    const { preview, truncated } = buildPreview(text);
    return { preview, collapsible: truncated && text.length - preview.length >= MIN_HIDDEN_CHARS };
  }, [text]);

  // Toggle expand/collapse. On *collapse* (the only direction that shrinks the
  // row and can strand the scroll) we snapshot the button's viewport Y before
  // React re-renders, then restore it once the layout — including Virtuoso's
  // own ResizeObserver reflow — has settled, by nudging the enclosing scroller.
  const handleToggle = () => {
    if (!expanded) {
      setExpanded(true);
      return;
    }
    const before = btnRef.current?.getBoundingClientRect().top ?? null;
    setExpanded(false);
    if (before === null) return;
    const restore = () => {
      const el = btnRef.current;
      // The scroller carries ``data-virtuoso-scroller``; bail gracefully if the
      // markup ever changes so a missing anchor can't throw.
      const scroller = el?.closest<HTMLElement>("[data-virtuoso-scroller]");
      if (!el || !scroller) return;
      const delta = el.getBoundingClientRect().top - before;
      if (Math.abs(delta) > 1) scroller.scrollTop += delta;
    };
    // Two rAFs clear React's commit and Virtuoso's resize reflow; the timeout
    // is a backstop in case the reflow lands a frame later. The second pass is
    // a no-op once the button already sits at its original Y (delta ≈ 0).
    requestAnimationFrame(() => requestAnimationFrame(restore));
    window.setTimeout(restore, 64);
  };

  if (!collapsible) {
    return <p className="whitespace-pre-wrap">{text}</p>;
  }

  return (
    <div>
      <p className="whitespace-pre-wrap">{expanded ? text : `${preview}…`}</p>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        className={`mt-1.5 inline-flex items-center gap-1 text-xs font-medium transition-colors
          ${onDark ? "text-white/80 hover:text-white" : "text-jove hover:text-jove-200"}`}
      >
        {expanded ? "Show less" : "Show more"}
        <svg
          className={`w-3 h-3 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
  );
}

// Heroicons ``hand-thumb-up`` outline path. Reused for thumbs-down by
// rotating 180°, so a single path serves both controls.
const THUMB_UP_PATH =
  "M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z";

/**
 * RLHF feedback state for an assistant message: 👍 / 👎 plus an optional
 * "what went wrong" note.
 *
 * Clicking a thumb submits the vote immediately (optimistic — see the store's
 * ``submitFeedback``). A thumbs-down auto-opens the note field, since that's
 * where the most actionable signal lives; a thumbs-up offers it on a second
 * click. We deliberately do NOT support un-voting: clicking the active thumb
 * just toggles the note editor, because an RLHF signal shouldn't silently
 * vanish once given. Persisted votes are restored from ``message.feedback`` on
 * reload, so the highlighted thumb survives a refresh.
 *
 * Exposed as a hook (rather than one self-contained component) so the thumbs
 * can sit inline in the message action row — right beside copy/read-aloud —
 * while the optional note editor renders on its own line below, where a
 * textarea actually fits. Both surfaces share this single state bundle.
 */
function useFeedback(message: Message) {
  const submitFeedback = useChatStore((s) => s.submitFeedback);
  const rating = message.feedback ?? null;
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(message.feedback_comment ?? "");
  const [thanks, setThanks] = useState(false);

  // Keep the local draft in sync if the server-restored comment changes
  // (e.g. history reload replaces the message object).
  useEffect(() => {
    setComment(message.feedback_comment ?? "");
  }, [message.feedback_comment]);

  const vote = (next: "like" | "dislike") => {
    if (rating === next) {
      // Re-click on the active thumb → toggle the note editor, don't un-vote.
      setShowComment((v) => !v);
      return;
    }
    void submitFeedback(message.id, next, comment.trim() || undefined);
    setShowComment(next === "dislike");
  };

  const saveComment = () => {
    if (!rating) return;
    void submitFeedback(message.id, rating, comment.trim() || undefined);
    setShowComment(false);
    setThanks(true);
    setTimeout(() => setThanks(false), 2000);
  };

  return { rating, showComment, setShowComment, comment, setComment, thanks, vote, saveComment };
}

type FeedbackState = ReturnType<typeof useFeedback>;

/**
 * 👍 / 👎 controls, rendered inline in the message action row next to the
 * copy and read-aloud buttons. The accompanying note editor lives in
 * {@link FeedbackNote}; both read from the same {@link useFeedback} bundle.
 */
function FeedbackThumbs({ feedback }: { feedback: FeedbackState }) {
  const { rating, vote, thanks } = feedback;
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => vote("like")}
        title="Good response"
        aria-label="Good response"
        aria-pressed={rating === "like"}
        className={`flex items-center p-1 rounded-md transition-colors ${
          rating === "like" ? "text-jove bg-jove-10" : "text-space-50 hover:text-space hover:bg-space-20"
        }`}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d={THUMB_UP_PATH} />
        </svg>
      </button>
      <button
        onClick={() => vote("dislike")}
        title="Bad response"
        aria-label="Bad response"
        aria-pressed={rating === "dislike"}
        className={`flex items-center p-1 mr-1.5 rounded-md transition-colors ${
          rating === "dislike" ? "text-red-500 bg-red-50" : "text-space-50 hover:text-space hover:bg-space-20"
        }`}
      >
        <svg className="w-3.5 h-3.5 rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d={THUMB_UP_PATH} />
        </svg>
      </button>
      {thanks && <span className="text-[10px] text-space-40 ml-1 mr-2">Thanks for the feedback!</span>}
    </div>
  );
}

/**
 * Optional "what went wrong" note editor for a thumbs rating. Renders nothing
 * until a thumb is active and the editor has been opened; sits on its own line
 * below the action row (see {@link FeedbackThumbs}).
 */
function FeedbackNote({ feedback }: { feedback: FeedbackState }) {
  const { rating, showComment, comment, setComment, saveComment, setShowComment } = feedback;
  if (!showComment || !rating) return null;
  return (
    <div className="flex flex-col gap-1">
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={rating === "dislike" ? "What went wrong? (optional)" : "Add a note (optional)"}
        rows={2}
        autoFocus
        className="w-full max-w-xs text-xs rounded-md border border-space-30 bg-surface px-2 py-1
                   text-space placeholder:text-space-40 focus:border-jove-100
                   focus:outline-none focus:ring-1 focus:ring-jove-60 resize-none"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={saveComment}
          className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-primary text-on-primary
                     hover:bg-jove-200 transition-colors"
        >
          Send
        </button>
        <button
          onClick={() => setShowComment(false)}
          className="text-[10px] text-space-50 hover:text-space"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Inline "model switched to X by Y" notice — a centered timeline divider that
 * marks *where* in the conversation a model switch happened and *who* did it
 * (the Auto Router, or a named person). Deliberately styled as a system event,
 * not a chat bubble, so it reads as a temporal marker. Backed by a persisted
 * ``model_switch`` row (see chatStore + SessionManager._announce_model_switch),
 * so it survives reload and stays anchored at the right point in history.
 */
function ModelSwitchDivider({ block }: { block: ModelSwitchBlock }) {
  const label = displayModelLabel(block.model) ?? block.model;
  const providerLabel = PROVIDER_DISPLAY[getProvider(block.model)];
  return (
    <div
      className="flex items-center gap-2.5 my-1 px-1 select-none"
      title={block.reason || undefined}
    >
      {/* Divider lines shrink to 0 rather than forcing the label off-screen:
          on a narrow (mobile) column the label wraps to 2 lines, centered,
          instead of overflowing past the right-hand line (which read as
          left-aligned). `min-w-0` lets the flex label actually wrap. */}
      <div className="flex-1 h-px bg-space-30 shrink" />
      <span className="flex items-center gap-1.5 min-w-0 text-[11px] leading-snug text-space-50 text-center">
        <ProviderIcon model={block.model} className="w-3 h-3 shrink-0 text-space-40" />
        <span className="min-w-0">
          Model switched to <span className="font-semibold text-space">{label}</span>
          {providerLabel ? ` via ${providerLabel}` : ""}
          {" · by "}
          <span className="font-medium text-space">{block.by}</span>
        </span>
      </span>
      <div className="flex-1 h-px bg-space-30 shrink" />
    </div>
  );
}

/**
 * Inline "environment switched to X by Y" notice — the same centered timeline
 * divider as {@link ModelSwitchDivider}, marking where a conversation was moved
 * between execution environments (cloud ⇄ a device). Backed by a persisted +
 * cross-device-synced ``env_switch`` row (see transfer route + chatStore), so it
 * survives reload and stays anchored at the point the move happened.
 */
function EnvSwitchDivider({ block }: { block: EnvSwitchBlock }) {
  const toCloud = !block.to || block.to === "cloud";
  return (
    <div className="flex items-center gap-2.5 my-1 px-1 select-none">
      <div className="flex-1 h-px bg-space-30 shrink" />
      <span className="flex items-center gap-1.5 min-w-0 text-[11px] leading-snug text-space-50 text-center">
        <svg className="w-3 h-3 shrink-0 text-space-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          {toCloud ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h11a3 3 0 00.5-5.96 5 5 0 00-9.78-1.63A3.5 3.5 0 003 15z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a1 1 0 011-1h14a1 1 0 011 1v9H4V6zM2 19h20l-1.5-2H3.5L2 19z" />
          )}
        </svg>
        <span className="min-w-0">
          Moved to <span className="font-semibold text-space">{block.to_label}</span>
          {" · by "}
          <span className="font-medium text-space">{block.by}</span>
        </span>
      </span>
      <div className="flex-1 h-px bg-space-30 shrink" />
    </div>
  );
}

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

/** Rehype plugin: wrap each word of streamed text in a fade-in span. A CSS
 *  animation fires once on mount, so React reuses the existing word spans (no
 *  replay) and only newly appended words fade — the Open-WebUI per-word stream
 *  effect, but over live markdown. Skips code/pre so highlighting isn't split. */
function rehypeFadeWords() {
  const walk = (node: HastNode, inCode: boolean) => {
    if (!node.children) return;
    const out: HastNode[] = [];
    for (const child of node.children) {
      if (child.type === "text" && !inCode) {
        for (const part of (child.value ?? "").split(/(\s+)/)) {
          if (part === "") continue;
          out.push(/\s/.test(part)
            ? { type: "text", value: part }
            : { type: "element", tagName: "span", properties: { className: ["fade-in-token"] }, children: [{ type: "text", value: part }] });
        }
      } else {
        if (child.type === "element") {
          walk(child, inCode || child.tagName === "code" || child.tagName === "pre");
        }
        out.push(child);
      }
    }
    node.children = out;
  };
  return (tree: HastNode) => walk(tree, false);
}

const MARKDOWN_COMPONENTS: Components = {
  table({ children }) {
    return (
      <div className="my-2 overflow-x-auto rounded-lg border border-space-30 shadow-sm">
        <table className="w-full text-xs border-collapse">{children}</table>
      </div>
    );
  },
  thead({ children }) {
    return <thead className="sticky top-0 bg-space-20">{children}</thead>;
  },
  th({ children }) {
    return (
      <th className="px-3 py-2 text-left font-semibold text-space border-b border-space-30 whitespace-nowrap">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="px-3 py-2 text-space border-b border-space-30/60">
        {children}
      </td>
    );
  },
  tr({ children, ...props }) {
    return (
      <tr className="odd:bg-surface even:bg-jove-30 hover:bg-jove-50 transition-colors" {...props}>
        {children}
      </tr>
    );
  },
  pre({ children }) {
    // The `code` handler fully renders block code (wrapper + CopyButton).
    // Pass through so we don't double-wrap with an outer <pre>.
    return <>{children}</>;
  },
  code({ className, children, ...props }) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const streaming = useContext(StreamingCtx);
    const codeString = String(children).replace(/\n$/, "");
    const match = /language-(\w+)/.exec(className || "");
    // react-markdown v9 dropped the `inline` prop. A node is a block if it has
    // a language class (fenced w/ lang) or spans multiple lines.
    const isBlock = !!match || codeString.includes("\n");

    if (isBlock) {
      return (
        <div className="relative group rounded-lg overflow-hidden border border-space-30 my-2 shadow-sm">
          <CopyButton text={codeString} />
          {match && !streaming ? (
            <Suspense fallback={<pre className="p-3 text-xs bg-jove-10 text-space font-mono leading-relaxed overflow-x-auto">{codeString}</pre>}>
              <LazyCodeBlock language={match[1]!} code={codeString} />
            </Suspense>
          ) : (
            // Mid-stream: plain <pre>, no Prism (avoids re-highlighting the
            // whole growing block every frame). Highlights once on finalise.
            <pre className="p-3 text-xs bg-jove-10 text-space font-mono leading-relaxed overflow-x-auto">{codeString}</pre>
          )}
        </div>
      );
    }

    return (
      <code
        className={`${className ?? ""} bg-jove-10 text-code-ink px-1 py-0.5 rounded text-xs font-mono break-words [overflow-wrap:anywhere]`}
        {...props}
      >
        {children}
      </code>
    );
  },
  a({ href, children }) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#5F2EEA",
          textDecoration: "underline",
          textDecorationColor: "rgba(95, 46, 234, 0.5)",
          textUnderlineOffset: "3px",
          textDecorationThickness: "2px",
          fontWeight: 500,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.textDecorationColor = "#5F2EEA";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.textDecorationColor = "rgba(95, 46, 234, 0.5)";
        }}
        onClick={(e) => {
          if (href) {
            e.preventDefault();
            fetch("/api/open-url", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ url: href }),
            }).catch(() => {
              window.open(href, "_blank", "noopener");
            });
          }
        }}
      >
        {children}
      </a>
    );
  },
  hr() {
    return <hr className="my-4 border-space-30" />;
  },
};

/** Assistant markdown body, shared by the finalised bubble and the live stream.
 *  ``fade`` adds the per-word fade-in (streaming only) over identical markup so
 *  the hand-off to the persisted bubble is seamless. */
/** A finished background delegation, rendered with the SAME quiet chrome as a
 *  tool-call card (chevron header, muted OUTPUT panel) instead of the gold
 *  system callout — per the "show it like Tasks" request. The header carries
 *  the two facts the callout buried: this ran in the BACKGROUND, and how it
 *  exited. Purely presentational: the agent-waking trigger is server-side
 *  (``_deliver_delegation_result``) and fires whether or not any UI is open.
 */
function DelegationReceipt({ message, text }: { message: Message; text: string }) {
  const [expanded, setExpanded] = useState(false);
  const drillInto = useSubagentDrillStore((s) => s.open);
  const run = useChatStore((s) =>
    s.subagentRuns.find((r) => r.tool_use_id === message.tool_use_id),
  );
  // The run row is the truth when present; the notice wording is the fallback
  // for rows synced from an environment whose ledger we don't have.
  const status =
    run?.status ??
    (/failed/i.test(text) ? "failed" : /stopped/i.test(text) ? "stopped" : "completed");
  const ok = status === "completed";
  const durationMs = run?.duration_ms ?? 0;
  const report = text.replace(/^🤖[^\n]*\n*/, "").trim();

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  return (
    // Full column width, like every other tool row (ToolCallGroup/Thoughts) —
    // bubble-width here made the card read as a misplaced message.
    <div className="w-full rounded-lg border border-space-20 bg-space-10/60 overflow-hidden">
        <div className="flex items-center">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-1 min-w-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-space-50 hover:text-space transition-colors overflow-hidden"
          >
            <svg
              className={`w-3 h-3 shrink-0 text-space-40 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="shrink-0">🤖</span>
            <span className="truncate">
              {run?.description || "Background sub-agent"}
              <span className="text-space-40 font-normal"> · background</span>
            </span>
            <span className="ml-auto flex items-center gap-1.5 shrink-0 text-space-40">
              {/* Success is the tick alone — the word beside it said nothing the
                  glyph didn't, and it read as a status chip on a card that has
                  already finished. Every OTHER outcome keeps its word: there is
                  no glyph for those, and "failed" is not a thing to infer. */}
              {ok ? (
                <svg
                  className="w-3 h-3 text-oxy" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" strokeWidth={2.5}
                  role="img" aria-label="Completed"
                >
                  <title>Completed</title>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="text-amber-600">{status}</span>
              )}
              {durationMs > 0 && (
                <span className="tabular-nums font-mono">{formatTime(durationMs)}</span>
              )}
            </span>
          </button>
          {/* Sibling, not nested — a button inside a button is invalid HTML. */}
          {message.tool_use_id ? (
            <button
              type="button"
              onClick={() => drillInto(message.tool_use_id!)}
              className="shrink-0 mr-2 px-2 py-1 text-[10px] font-medium rounded
                         text-jove hover:bg-jove-30 transition-colors"
              title="View this sub-agent's activity"
            >
              View activity
            </button>
          ) : null}
        </div>
        {expanded && report && (
          <div className="px-3 pb-3 pt-2.5 border-t border-space-20/70 animate-[slideDown_250ms_ease-out]">
            <p className="text-[10px] uppercase tracking-wider text-space-40 font-semibold mb-1">Output</p>
            <pre className="text-xs rounded-lg p-2.5 overflow-x-auto font-mono max-h-48 overflow-y-auto leading-relaxed bg-surface border border-space-30 text-space-50 whitespace-pre-wrap">
              {report.length > 2000 ? report.slice(0, 2000) + "\n... (truncated)" : report}
            </pre>
          </div>
        )}
    </div>
  );
}

function AssistantMarkdown({ text, fade = false }: { text: string; fade?: boolean }) {
  return (
    <StreamingCtx.Provider value={fade}>
      <div className={ASSISTANT_PROSE_CLASS}>
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={fade ? [rehypeFadeWords] : undefined}
          components={MARKDOWN_COMPONENTS}
        >
          {text}
        </Markdown>
      </div>
    </StreamingCtx.Provider>
  );
}

/**
 * Render a single message. Memoised on ``message.id`` + content because
 * Virtuoso mounts/unmounts rows as the user scrolls — without the memo the
 * markdown re-renders for every scroll tick a row enters the viewport.
 * Equality compares the immutable fields rendered below; the streaming
 * append path overwrites the whole message object so identity check is
 * sufficient for the live update case.
 */
function MessageBubbleImpl({ message, streaming = false, entranceAnimation = true, highlight = false, hideFeedback = false }: Props) {
  const text = useMemo(() => {
    // content_json is supposed to be a JSON-encoded array of content blocks,
    // but legacy/corrupted rows may hold a raw string, an object, or a
    // primitive. Anything that isn't an array of blocks falls back to
    // wrapping the raw value as a single text block — never crash render.
    let blocks: { type: string; text?: string }[];
    try {
      const parsed = JSON.parse(message.content_json);
      blocks = Array.isArray(parsed)
        ? parsed
        : [{ type: "text", text: typeof parsed === "string" ? parsed : message.content_json }];
    } catch {
      blocks = [{ type: "text", text: message.content_json }];
    }
    return blocks
      .filter((b) => b && b.type === "text")
      .map((b) => b.text ?? "")
      .join("\n");
  }, [message.content_json]);

  // Detect shortlist JSON — render a card widget instead of raw text.
  // The agent may output markdown text before the JSON block, so we
  // search for the first '{' that parses into a valid shortlist object.
  const { shortlistData, textBeforeShortlist } = useMemo<{
    shortlistData: ShortlistData | null;
    textBeforeShortlist: string;
  }>(() => {
    if (message.role !== "assistant") return { shortlistData: null, textBeforeShortlist: "" };
    // Fast check: must contain "evaluations" somewhere
    if (!text.includes('"evaluations"')) return { shortlistData: null, textBeforeShortlist: "" };

    // Try whole text first (pure JSON message)
    try {
      const parsed = JSON.parse(text.trim());
      if (parsed?.evaluations && Array.isArray(parsed.evaluations)) {
        return { shortlistData: parsed, textBeforeShortlist: "" };
      }
    } catch { /* not pure JSON */ }

    // Find embedded JSON: look for first '{' that parses into a shortlist
    const firstBrace = text.indexOf("{");
    if (firstBrace > 0) {
      try {
        const parsed = JSON.parse(text.slice(firstBrace));
        if (parsed?.evaluations && Array.isArray(parsed.evaluations)) {
          return { shortlistData: parsed, textBeforeShortlist: text.slice(0, firstBrace).trim() };
        }
      } catch { /* not valid JSON from first brace */ }
    }

    return { shortlistData: null, textBeforeShortlist: "" };
  }, [text, message.role]);

  // RLHF feedback state. Declared here (before the early returns below) so it
  // obeys the rules of hooks; only assistant rows actually surface the thumbs
  // and note (see the action row). The shared bundle lets the thumbs render
  // inline next to copy while the note editor renders on its own line below.
  const feedback = useFeedback(message);

  // Inline model-switch notice. Checked before the empty-text guard below
  // because its content block carries no "text" block (so ``text`` is empty).
  if (message.role === "model_switch") {
    let block: ModelSwitchBlock | null = null;
    try {
      const parsed = JSON.parse(message.content_json);
      block = Array.isArray(parsed)
        ? ((parsed.find((b) => b?.type === "model_switch") as ModelSwitchBlock | undefined) ?? null)
        : null;
    } catch { /* malformed row — render nothing */ }
    if (!block?.model) return null;
    return <ModelSwitchDivider block={block} />;
  }

  // Inline environment-switch notice. Like model_switch, its content block
  // carries no "text" block, so it's checked before the empty-text guard.
  if (message.role === "env_switch") {
    let block: EnvSwitchBlock | null = null;
    try {
      const parsed = JSON.parse(message.content_json);
      block = Array.isArray(parsed)
        ? ((parsed.find((b) => b?.type === "env_switch") as EnvSwitchBlock | undefined) ?? null)
        : null;
    } catch { /* malformed row — render nothing */ }
    if (!block?.to_label) return null;
    return <EnvSwitchDivider block={block} />;
  }

  // Persisted extended-thinking row — a collapsed "Thought for Ns" disclosure
  // anchored above the answer it produced. Checked before the empty-text guard
  // because a thinking row carries a ``thinking`` block, not a ``text`` one.
  if (message.role === "thinking") {
    let block: ThinkingContentBlock | null = null;
    try {
      const parsed = JSON.parse(message.content_json);
      block = Array.isArray(parsed)
        ? ((parsed.find((b) => b?.type === "thinking") as ThinkingContentBlock | undefined) ?? null)
        : null;
    } catch { /* malformed row — render nothing */ }
    if (!block?.thinking?.trim()) return null;
    // Full-width, like tool rows — fills the shared max-w-3xl column rather than
    // sitting in an 80% assistant-bubble box.
    return <MessageThinkingPanel text={block.thinking} durationMs={block.duration_ms} />;
  }

  if (!text.trim()) return null;

  if (streaming) {
    return (
      <div className={`flex justify-start ${entranceAnimation ? "animate-[fadeInUp_200ms_ease-out]" : ""}`}>
        <div className={`group ${BUBBLE_WIDTH} rounded-2xl px-4 py-3 text-sm leading-relaxed overflow-hidden break-words bg-space-20 text-space rounded-bl-md`}>
          <AssistantMarkdown text={text} fade />
        </div>
      </div>
    );
  }

  if (shortlistData) {
    return (
      <>
        {textBeforeShortlist && (
          <div className="flex justify-start animate-[fadeInUp_200ms_ease-out]">
            <div className={`${BUBBLE_WIDTH} rounded-2xl px-4 py-3 text-sm leading-relaxed bg-surface border border-space-20 text-space`}>
              <Markdown remarkPlugins={[remarkGfm]}>{textBeforeShortlist}</Markdown>
            </div>
          </div>
        )}
        <ShortlistReadyCard data={shortlistData} />
      </>
    );
  }

  // A finished delegation's receipt renders as a tool-style card, not a gold
  // callout — the structured tag (tool_name column) is what routes it here.
  if (message.role === "system" && message.tool_name === "delegate") {
    return <DelegationReceipt message={message} text={text} />;
  }

  const isUser = message.role === "user";
  const isQueued = isUser && message.id.startsWith("queued-");
  const isSystem = message.role === "system";
  const isResult = message.role === "result";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} ${entranceAnimation ? "animate-[fadeInUp_200ms_ease-out]" : ""}`}>
      <div
        className={`group ${BUBBLE_WIDTH} rounded-2xl px-4 py-3 text-sm leading-relaxed overflow-hidden break-words
          ${
            isQueued
              ? "bg-jove-50 text-space border border-jove-60/40 border-dashed rounded-br-md"
              : isUser
                ? "bg-jove text-white rounded-br-md shadow-sm"
                : isSystem
                  ? "bg-gold/10 text-gold border border-gold/20 rounded-lg"
                  : isResult
                    ? "bg-oxy-50 text-space border border-oxy/20 rounded-lg"
                    : "bg-space-20 text-space rounded-bl-md"
          }
          ${highlight ? "animate-[messageFlash_1.6s_ease-out]" : ""}`}
      >
        {isQueued ? (
          <div>
            <CollapsibleText text={text} onDark={false} />
            <p className="text-[10px] mt-1.5 text-space-50 font-medium">Queued — will send when agent finishes</p>
          </div>
        ) : isUser ? (
          <CollapsibleText text={text} onDark={true} />
        ) : (
          <AssistantMarkdown text={text} />
        )}

        {/* Cost/duration info for result messages */}
        {isResult && (message.cost_usd != null || message.pplx_cost_usd != null) && (
          <div className="mt-2 pt-2 border-t border-oxy/20 text-xs text-space-40 flex items-center gap-3">
            {(message.cost_usd != null || message.pplx_cost_usd != null) && (() => {
              const claude = message.cost_usd || 0;
              const pplx = message.pplx_cost_usd || 0;
              const total = claude + pplx;
              if (total <= 0) return null;
              if (pplx > 0) {
                return (
                  <span className="font-mono tabular-nums">
                    ${claude.toFixed(2)} Claude + ${pplx.toFixed(2)} Perplexity = ${total.toFixed(2)}
                  </span>
                );
              }
              return <span className="font-mono tabular-nums">${claude.toFixed(4)}</span>;
            })()}
            {message.duration_ms != null && (
              <span className="font-mono tabular-nums">{(message.duration_ms / 1000).toFixed(1)}s</span>
            )}
          </div>
        )}

        {/* Action row: copy + (assistant only) read-aloud + 👍/👎 on the left,
            model badge + timestamp on the right. The optional feedback note
            renders on its own line below — assistant answers only — since a
            textarea can't share the action row's single-line height. */}
        {message.created_at && (
          <div className="flex flex-col gap-1 mt-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Queued bubbles use themed (not solid-jove) fills, so the
                    copy button must use the light-surface ink, not the
                    white-on-jove treatment reserved for sent user bubbles. */}
                <BubbleCopyButton text={text} isUser={isUser && !isQueued} />
                {!isUser && !isQueued && !isSystem && !isResult && <SpeakButton text={text} />}
                {/* RLHF thumbs — assistant answers only. Excluded from user,
                    queued, system, and result rows (none are model answers to
                    rate). Sits inline with copy/read-aloud for one tidy row. */}
                {!isUser && !isQueued && !isSystem && !isResult && !hideFeedback && <FeedbackThumbs feedback={feedback} />}
              </div>
              <div className="flex items-center gap-2">
                {/* Provider logo badge with hover tooltip — shown only for
                    non-user rows where ``messages.model`` is set and we
                    recognise the provider. Pre-v15 NULL rows and unknown
                    providers render nothing, which is the right graceful
                    fallback for customers upgrading from older installs.
                    ``title`` is the native HTML tooltip (works on every
                    browser, no JS); ``aria-label`` mirrors it for screen
                    readers. */}
                {!isUser && !isQueued && (() => {
                  const provider = getProvider(message.model);
                  if (provider === "unknown") return null;
                  const label = displayModelLabel(message.model) ?? message.model ?? "";
                  return (
                    <span
                      className="inline-flex items-center justify-center w-4 h-4 text-space-50
                                 hover:text-space transition-colors cursor-default"
                      title={label}
                      aria-label={`Generated by ${label}`}
                    >
                      <ProviderIcon model={message.model} className="w-3.5 h-3.5" />
                    </span>
                  );
                })()}
                <p className={`text-[10px] tabular-nums ${isUser ? "text-white/50" : "text-space-40"}`}>
                  {/* Prefer the TRUE send time (#84): for a queued message,
                      created_at records when the turn finally persisted it —
                      minutes after the user hit send — and every window used
                      to disagree on it. */}
                  {parseUTC(message.sent_at ?? message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
            {!isUser && !isQueued && !isSystem && !isResult && !hideFeedback && <FeedbackNote feedback={feedback} />}
          </div>
        )}
      </div>
    </div>
  );
}

export const MessageBubble = memo(
  MessageBubbleImpl,
  // Streaming overwrites the whole message object, so ``===`` on the few
  // fields we render is enough. We compare the JSON blob directly because
  // ``assistant_text`` updates only mutate ``content_json``.
  (prev, next) =>
    prev.streaming === next.streaming
    && prev.entranceAnimation === next.entranceAnimation
    && prev.highlight === next.highlight
    && prev.message.id === next.message.id
    && prev.message.role === next.message.role
    && prev.message.content_json === next.message.content_json
    && prev.message.created_at === next.message.created_at
    && prev.message.tool_name === next.message.tool_name
    && prev.message.tool_use_id === next.message.tool_use_id
    // Include ``model`` so a row that finally learns its model (e.g.
    // REST catch-up overwrites a streamed row that had ``model=null``)
    // re-renders to surface the badge.
    && prev.message.model === next.message.model
    // Re-render when the user's RLHF vote / note changes so the thumb
    // highlight and note editor reflect the latest state.
    && prev.message.feedback === next.message.feedback
    && prev.message.feedback_comment === next.message.feedback_comment,
);
